import {BaseCard} from "../../Components/Cards/BaseCard";
import {useEffect, useState} from "react";
import * as yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {DateTimePickerInput} from "../../Components/Form/DateTimePickerInput";
import * as React from "react";
import {SelectInput} from "../../Components/Form/SelectInput";
import {useBoundStore} from "../../Store/store";
import {useTranslation} from "react-i18next";
import dayjs from "dayjs";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faDownload, faArrowRight} from "@fortawesome/free-solid-svg-icons";
import {BlackButton} from "../../Components/Buttons/BlackButton";
import {MonthlyInvoicedChart} from "../../Components/Charts/MonthlyInvoicedChart";
import {Link} from "react-router-dom";
import {truncateString} from "../../Utils/StringHelper";

export const Statistics = () => {
    const {t} = useTranslation();
    const [loading, setLoading] = useState(false);
    const [customerBranches, setCustomerBranches] = useState([]);

    const customers = useBoundStore((state) => state.customers)
    const shiftStatistics = useBoundStore((state) => state.shiftStatistics)
    const monthInvoicedStatistics = useBoundStore((state) => state.monthInvoicedStatistics)
    const getAllCustomers = useBoundStore((state) => state.getAllCustomers);
    const getStatisticsForCustomer = useBoundStore((state) => state.getStatisticsForCustomer);
    const getExportForStatisticsForCustomer = useBoundStore((state) => state.getExportForStatisticsForCustomer);

    const schema = yup.object({
        customer_id: yup.mixed().required(),
        start_time: yup.string().required(),
        end_time: yup.string().required(),
        branch_id: yup.mixed().nullable(),
    }).required();

    const {control, setValue, getValues, formState:{errors}, trigger, watch, register} = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            customer_id: null,
            start_time: dayjs().subtract(5, 'month').format('YYYY-MM-DD HH:mm:ss'),
            end_time: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            branch_id: null,
        }
    });

    const customerId = watch('customer_id');
    const startTime = watch('start_time');
    const endTime = watch('end_time');
    const branchId = watch('branch_id');

    useEffect(() => {
        getAllCustomers(null, null, {nopaginate: true});
    }, [getAllCustomers]);

    useEffect(() => {
        if (customerId && customers.length > 0) {
            const customer = customers.find(customer => customer.id === customerId);
            if (customer) {
                setCustomerBranches(customer?.branches?.map((branch) => {
                    return {id: branch.id, value: branch.id, label: branch.company_name}
                }) || []);

                if (customer.branches && customer.branches.length === 1) {
                    setValue('branch_id', customer.branches[0].id, { shouldValidate: true });
                }
            }
        }
    }, [customerId, customers, setValue]);

    useEffect(() => {
        if(customerId && startTime && endTime && branchId) {
            trigger().then(r => {
                if(r) {
                    setLoading(true);

                    (async () => {
                        try {
                            await getStatisticsForCustomer(
                                customerId,
                                dayjs(startTime).unix(),
                                dayjs(endTime).unix(),
                                branchId
                            )
                            setLoading(false)
                        } finally {
                            setLoading(false)
                        }
                    })()
                } else {
                    setLoading(false);
                }
            });
        } else {
            setLoading(false);
        }
    }, [customerId, startTime, endTime, branchId]);

    const downloadExcel = async () => {
        const data = await getExportForStatisticsForCustomer(
            customerId,
            dayjs(startTime).unix(),
            dayjs(endTime).unix(),
        );
        const outputFilename = 'statistics-shifts.xlsx';

        console.log(data);

        const url = URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', outputFilename);
        document.body.appendChild(link);
        link.click();
    }

    return (
        <div className={'grid grid-cols-1 gap-4'}>
            <BaseCard>
                <div className={'grid grid-cols-1 md:grid-cols-4 gap-4'}>
                    <SelectInput
                        name={'customer_id'}
                        options={customers.map((customer) => {
                            return {id: customer?.id, value: customer?.id, label: `${customer?.firstname} ${customer?.lastname}`}
                        })}
                        control={control}
                        value={getValues('customer_id') ?? []}
                        setValue={(e) => {
                            setCustomerBranches([]);
                            setValue('branch_id', null);
                            setValue('customer_id', e, { shouldValidate: true });
                        }}
                        placeholder={t('general.fields.client')}
                        translationIndex={'shifts.client'}
                    />

                    <SelectInput
                        name={'branch_id'}
                        options={customerBranches}
                        register={register}
                        control={control}
                        value={getValues('branch_id') ?? []}
                        setValue={setValue}
                        translationIndex={t('tables.branch')}
                        clearable={true}
                    />

                    <DateTimePickerInput
                        label={'Start time'}
                        name={'start_time'}
                        setValue={setValue}
                        control={control}
                        currentValue={getValues('start_time')}
                        translationIndex={'general.fields.start_time'}
                        errors={errors}
                    />
                    <DateTimePickerInput
                        label={'End time'}
                        name={'end_time'}
                        setValue={setValue}
                        control={control}
                        currentValue={getValues('start_time')}
                        translationIndex={'general.fields.end_time'}
                        errors={errors}
                    />
                </div>
            </BaseCard>

            <BaseCard>
                <h2 className={'text-lg text-gray-700 uppercase mb-4 mt-2'}>{t('charts.invoices_per_month', {year: dayjs().year()})}</h2>

                {
                    !customerId && !loading && (
                        <div className={'flex flex-col justify-center items-center py-12'}>
                            <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <p className="text-gray-600 text-lg">{t('charts.select_customer')}</p>
                        </div>
                    )
                }

                {
                    customerId && !loading && monthInvoicedStatistics?.hasOwnProperty('jan') && (
                        <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                            <MonthlyInvoicedChart data={monthInvoicedStatistics ?? []}/>
                            <table className={'ml-8'}>
                                {['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].map((month, index) => {
                                    return (
                                        <tr key={index}>
                                            <td className={'px-5 py-1 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider'}>{t(`months.${month}`)}</td>
                                            <td className={'pl-4'}>{new Intl.NumberFormat('nl', {
                                                style: 'currency',
                                                currency: 'EUR'
                                            }).format(monthInvoicedStatistics[month])}</td>
                                        </tr>
                                    )
                                })}
                            </table>
                        </div>
                    )
                }

                {
                    customerId && loading && (
                        <div className={'flex flex-col justify-center items-center py-12'}>
                            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mb-4"></div>
                            <p className="text-gray-600">{t('general.messages.loading_data')}</p>
                        </div>
                    )
                }
            </BaseCard>

            <BaseCard>
                {
                    !customerId && !loading && (
                        <div className={'flex flex-col justify-center items-center py-12'}>
                            <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p className="text-gray-600 text-lg">{t('charts.select_customer_shifts')}</p>
                        </div>
                    )
                }

                {
                    customerId && !loading && (
                        <>
                            <div className={'flex flex-row justify-end mb-4'}>
                                <div>
                                    <BlackButton onClick={downloadExcel}>
                                        <FontAwesomeIcon icon={faDownload} className={'mr-2'}/> Download
                                    </BlackButton>
                                </div>
                            </div>

                            {
                                shiftStatistics.length > 0 && (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full leading-normal">
                                            <thead>
                                                <tr>
                                                    <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                        {t('tables.start')}
                                                    </th>
                                                    <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                        {t('tables.end')}
                                                    </th>
                                                    <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                        {t('tables.customer')}
                                                    </th>
                                                    <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                        {t('shifts.freelancer')}
                                                    </th>
                                                    <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                        {t('tables.actions')}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {shiftStatistics.map((shift) => (
                                                    <tr key={shift.id}>
                                                        <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                                                            {dayjs(shift.start_time).format('DD/MM/YYYY HH:mm')}
                                                        </td>
                                                        <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                                                            {dayjs(shift.end_time).format('DD/MM/YYYY HH:mm')}
                                                        </td>
                                                        <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                                                            {truncateString(shift.branch?.company_name ?? '-')}
                                                        </td>
                                                        <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                                                            {truncateString(`${shift.freelancer?.first_name ?? '-'} ${shift.freelancer?.last_name ?? ''}`)}
                                                        </td>
                                                        <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                                                            <Link
                                                                to={`/admin/shifts/${shift.id}`}
                                                                className={'flex bg-primary w-7 h-7 rounded-full text-white flex-col justify-center border border-primary hover:bg-transparent hover:border-primary hover:text-primary transition-all duration-300'}
                                                            >
                                                                <FontAwesomeIcon icon={faArrowRight} className={'text-sm'}/>
                                                            </Link>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )
                            }

                            {
                                shiftStatistics.length == 0 && (
                                    <div className={'flex flex-row justify-center items-center py-12'}>
                                        <p className="text-gray-600">{t('general.messages.no_results')}</p>
                                    </div>
                                )
                            }
                        </>
                    )
                }

                {
                    customerId && loading && (
                        <div className={'flex flex-col justify-center items-center py-12'}>
                            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mb-4"></div>
                            <p className="text-gray-600">{t('general.messages.loading_data')}</p>
                        </div>
                    )
                }
            </BaseCard>
        </div>
    )
}